import React, { useState, useEffect, useRef, useMemo } from "react";
import NavigoAPIServices from "../services/navigoAPIServices";
import EventService from "../../../../../service/event/EventService";
import PatientsService from "../../../../ops/modules/patients/service/PatientsService";
import CommonService from "../../../../ops/modules/patients/modules/new-bridge/services/CommonService";
import BankAccountService from "../../../../ops/modules/patients/modules/new-bridge/services/BankAccountService";
import BankAccount from "../../../../ops/modules/patients/modules/new-bridge/components/BankAccount";
import { errorMessageHandler } from "../../disqualification-module/utils/helperMethod";
import ThemedDropdown from "./ThemedDropdown";
import "../styles/FileNewClaimScreen.scss";

const api = new NavigoAPIServices();
const patientService = new PatientsService();
const commonServices = new CommonService();
const bankAccountService = new BankAccountService();

// Bank-account status chip labels/classes — mirrors the claim-details screen.
const BANK_STATUS_LABEL = { 0: "Unverified", 1: "Verified", 2: "Rejected" };
const BANK_STATUS_CLASS = { 0: "unverified", 1: "verified", 2: "rejected" };
const maskedBankNum = (last4, accNumLength) =>
  "X".repeat(Math.max(0, (accNumLength || 0) - 4)) + (last4 ?? "");

// Standalone full-screen "File new claim" flow (replaces FileNewClaimDialog on the
// reimbursement/AI dashboard). Same data + submit flow as the dialog; new layout
// mirrors the redesigned screen — patient/claim cards, required-docs checklist,
// soft/hard-copy document type, and a document grid.

const DOC_TYPE = { SOFTCOPY: 1, HARDCOPY: 2 };

// The "Sublimit exception applicable" feature is temporarily disabled — the
// checkbox is hidden and the flag stays false. Flip to true to bring it back.
const SHOW_SUBLIMIT_EXCEPTION = false;

// Placeholder tiles rendered in the document grid until this many files are
// attached. Purely cosmetic — any number of documents can be uploaded.
const MIN_DOC_SLOTS = 8;

const getDocumentFormat = (type) => {
  switch ((type || "").toLowerCase()) {
    case "jpg":
    case "jpeg":
      return 1;
    case "png":
      return 2;
    case "gif":
      return 3;
    case "pdf":
      return 4;
    case "mp3":
      return 5;
    case "mp4":
      return 6;
    default:
      return 0;
  }
};

const getMinInvoiceDate = (minDaysInvoice) => {
  const today = new Date();
  const minDate = new Date(today.setDate(today.getDate() - minDaysInvoice));
  return minDate.toISOString().split("T")[0];
};

const getMaxInvoiceDate = () => new Date().toISOString().split("T")[0];

const extOf = (name) => (name || "").split(".").pop().toUpperCase();

const isImageFile = (doc) => {
  if (!doc) return false;
  const t = doc.fileObject && doc.fileObject.type;
  if (t && t.indexOf("image/") === 0) return true;
  return /\.(jpe?g|png|gif|webp|bmp)$/i.test(doc.name || "");
};

// The short-lived document access token (minted on mount, stored in localStorage)
// used to view server-side documents as `url + "?token=" + token`.
const getDocToken = () => {
  try {
    return (
      JSON.parse(localStorage.getItem("zy.bridge.doc.token") || "{}").token ||
      ""
    );
  } catch (e) {
    return "";
  }
};

const BackArrow = () => (
  <svg width="20" height="20" viewBox="0 0 21 21" fill="none">
    <path
      d="M12 15.25L6.75 10L12 4.75"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15.25 10H6.75"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const InfoIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M12 10.75v5.25"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <circle cx="12" cy="7.75" r="1.1" fill="currentColor" />
  </svg>
);

const WalletIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
    <rect
      x="3"
      y="6"
      width="18"
      height="13"
      rx="2.5"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path d="M3 9.5h18" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="16.5" cy="13.5" r="1.4" fill="currentColor" />
  </svg>
);
const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path
      d="M13 4.5L6.5 11L3 7.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const FileIcon = () => (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
    <path
      d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5z"
      stroke="#8b93a3"
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
    <path
      d="M14 3v5h5"
      stroke="#8b93a3"
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
  </svg>
);
const RetryIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M20 11a8 8 0 1 0-.5 3"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <path
      d="M20 5v4h-4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const arrow = (
  <svg width="18" height="18" viewBox="0 0 21 21" fill="none">
    <path
      d="M8 4.75L13.25 10L8 15.25"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const UploadIcon = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 15V3.5"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7.5 8L12 3.5L16.5 8"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4.5 14.5V18A2.5 2.5 0 0 0 7 20.5H17A2.5 2.5 0 0 0 19.5 18V14.5"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function FileNewClaimScreen(props) {
  const [token, setToken] = useState("");
  const [form, setForm] = useState({ documentType: DOC_TYPE.SOFTCOPY });
  const [plansArr, setPlansArr] = useState([]);
  const [accountConfigs, setAccountConfigs] = useState([]);
  const [claimTypesArr, setClaimTypesArr] = useState([]);
  const [claimConfig, setClaimConfig] = useState({});
  const [limitRes, setLimitRes] = useState(null);
  const [relations, setRelations] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [filteredFamily, setFilteredFamily] = useState([]);
  const [instructions, setInstructions] = useState([]);
  const [docs, setDocs] = useState([]);
  const [bankResp, setBankResp] = useState(null);
  const [openBankModal, setOpenBankModal] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftPendingSave, setDraftPendingSave] = useState(false);
  // Synchronous guard against a double-click firing two draft saves before the
  // button's `disabled` (bound to savingDraft state) has re-rendered.
  const savingDraftRef = useRef(false);
  // Same idea for submit: guards createReimbursement against a double file (the
  // upload-settled effect can fire while a create is already in flight).
  const submittingRef = useRef(false);
  const [draft, setDraft] = useState(null);
  // Tracks whether the user has edited anything since load/last save. Set only on
  // real user onChange events (draft restore mutates state directly, never via
  // onChange), so it stays false while a resumed draft is being prefilled.
  const [dirty, setDirty] = useState(false);
  const markDirty = () => setDirty(true);
  // Delete-draft flow (resume mode only): confirm modal + in-flight state.
  const [confirmDeleteDraft, setConfirmDeleteDraft] = useState(false);
  const [deletingDraft, setDeletingDraft] = useState(false);
  // Claim eligibility (wallet-computed). The create endpoint REJECTS the claim
  // unless the payload's eligibleClaimAmount exactly equals the wallet's current
  // eligibility — so we fetch it here, show it, and send it back verbatim.
  const [elig, setElig] = useState(null);
  const [showEligInfo, setShowEligInfo] = useState(false);
  const [fetchingElig, setFetchingElig] = useState(false);

  const uploadRef = useRef(null);

  // Resume-draft support: ?draftId=&pid= loads an existing DRAFT into the form,
  // which the user finishes and submits (a real claim is created, the draft deleted).
  const draftParams = new URLSearchParams(
    (props.location && props.location.search) || "",
  );
  const draftId = draftParams.get("draftId");
  const draftPid = draftParams.get("pid");
  // View-only mode — opened from the dashboard's eye icon (?view=1). The whole form
  // renders but nothing is editable (mirrors the claim-details view mode).
  const readOnly = draftParams.get("view") === "1";
  const draftApplied = useRef({ started: false, plan: false, type: false });

  const updateForm = (updates) => setForm((prev) => ({ ...prev, ...updates }));

  // Fetch wallet eligibility whenever its inputs change (debounced — claim amount
  // is typed). Sends the SAME fields the create endpoint recomputes with
  // (patientId, claimedFor, planId, claimTypeId, claimAmount) so the eligibleAmount
  // we get back matches what create checks against, and we send it back verbatim.
  useEffect(() => {
    const pid = form?.pid;
    const planId = form?.selectedPlan?.planDetails?.id;
    const typeId = form?.reimbursementTypeId;
    const claimedFor = form?.claimedFor;
    const amt = Number(form?.claimAmount);
    if (
      !pid ||
      !planId ||
      !typeId ||
      !(amt > 0) ||
      claimedFor === undefined ||
      claimedFor === null
    ) {
      setElig(null);
      return undefined;
    }
    setFetchingElig(true);
    const t = setTimeout(() => {
      api.fetchCurrAvailableClaimAmt(
        {
          patientId: parseInt(pid, 10),
          claimedFor: claimedFor,
          planId: planId,
          claimTypeId: Number(typeId),
          claimAmount: amt,
          // "Sublimit exception applicable": drives the preview eligibleAmount so
          // the value we submit matches the create endpoint's own recompute (which
          // reads this same flag) — otherwise the sublimit-capped amount would
          // mismatch and the create is rejected.
          ignoreReimbursementLimit: Boolean(form?.ignoreReimbursementLimit),
        },
        (res) => {
          setElig(res || null);
          setFetchingElig(false);
        },
        () => {
          setElig(null);
          setFetchingElig(false);
        },
      );
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form?.pid,
    form?.claimedFor,
    form?.selectedPlan?.planDetails?.id,
    form?.reimbursementTypeId,
    form?.claimAmount,
    form?.ignoreReimbursementLimit,
  ]);

  // Leaving this screen (back/cancel, draft deleted, claim filed) always lands on the
  // claims dashboard. Never history.goBack(): a pop goes to whatever the previous
  // history entry happens to be, and most Bridge navigation (Helper.navigateTo, used
  // by the drawer and the Miscellaneous tiles) *replaces* entries instead of pushing
  // them, so the entry under this screen is often the Bridge home page rather than
  // the dashboard. `dashboardSearch` carries the list's page/filters, which the
  // dashboard seeds its state from, so the view is restored without popping.
  // `replace`, not `push`, so browser-Back can't return to a form that was already
  // submitted (and re-file the claim).
  const goToDashboard = () => {
    if (!props.history) return;
    props.history.replace({
      pathname: "/miscellaneous/reimbursement-dashboard",
      search:
        (props.location &&
          props.location.state &&
          props.location.state.dashboardSearch) ||
        "",
    });
  };

  // Permanently delete the draft being resumed, then return to the dashboard.
  const handleDeleteDraft = () => {
    if (deletingDraft || !draftId) return;
    setDeletingDraft(true);
    api.deleteClaimDraft(
      draftId,
      () => {
        setDeletingDraft(false);
        setConfirmDeleteDraft(false);
        EventService.emit("showError", { message: "Draft deleted" });
        goToDashboard();
      },
      (err) => {
        setDeletingDraft(false);
        EventService.emit("showError", {
          message: err?.message ?? "Failed to delete draft.",
        });
      },
    );
  };

  // ---- Fetches ----
  useEffect(() => {
    patientService.getAndSaveDocumentToken(
      (res) => {
        localStorage.setItem("zy.bridge.doc.token", JSON.stringify(res));
        setToken((res && res.token) || "");
      },
      () => {},
    );
    patientService.getAllRelations(
      (res) => res && setRelations(res),
      () =>
        EventService.emit("showError", {
          message: "Cannot retrieve relations",
        }),
    );
    api.getAccountConfigs(
      (res) =>
        setAccountConfigs(Array.isArray(res) ? res : (res && res.data) || []),
      () => {},
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getFilteredPlans = (res) =>
    (res || []).filter(
      (e) =>
        e?.planDetails?.reimbursementDetails?.isReimbursementIncluded == true,
    );

  // Stable patient object for the reused BankAccount module (avoids refetch loops).
  const bankPatient = useMemo(() => ({ id: form.pid }), [form.pid]);

  // The plan decides whether a bank account is part of filing at all. When its
  // reimbursementDetails say it isn't required the claim isn't paid out to a bank
  // account, so the bank row (and the module behind it) stays hidden and no
  // bankAccountId is sent. Unknown until a plan is picked, hence false by default.
  const bankRequired = Boolean(
    form.selectedPlan?.planDetails?.reimbursementDetails?.isBankAccountRequired,
  );

  // Masked primary bank account for the selected patient — drives the summary chip
  // and the claim's bankAccountId. A missing account (404) is normal (ops can add one
  // via the module), so it silently clears rather than toasting.
  const refreshMaskedBank = (pid) => {
    if (!pid) {
      setBankResp(null);
      return;
    }
    bankAccountService.fetchMaskedPrimaryBankAccountForPatient(
      pid,
      (res) => setBankResp(res),
      () => setBankResp(null),
    );
  };

  // Cascade clear: wipe every below-plan field so changing an upper selector
  // (Patient ID / Account / Plan) never leaves stale claim data beneath it.
  // Per product decision, document type resets to the Softcopy default too.
  const clearClaimDetailsBelowPlan = () => {
    setForm((prev) => ({
      ...prev,
      claimedFor: undefined,
      reimbursementTypeId: undefined,
      reimbursementTypeName: undefined,
      invoiceDate: undefined,
      claimAmount: undefined,
      ignoreReimbursementLimit: false,
      documentType: DOC_TYPE.SOFTCOPY,
    }));
    setClaimTypesArr([]);
    setClaimConfig({});
    setInstructions([]);
    setLimitRes(null);
    setDocs([]);
    setElig(null);
  };

  const loadPatient = () => {
    const pid = form.pid;
    if (!pid || `${pid}`.length === 0) return;
    // Fresh lookup — clear account/plan, then cascade-clear everything below.
    setForm((prev) => ({ pid: prev.pid }));
    setPlansArr([]);
    setFilteredFamily([]);
    clearClaimDetailsBelowPlan();

    api.getPatientDataThroughID(
      pid,
      (res) => {
        if (res && res.id !== undefined) {
          const pName = `${res.firstName ?? ""} ${res.lastName ?? ""}`.trim();
          updateForm({ patientName: pName, clientCode: res.clientCode });
          refreshMaskedBank(pid);
          api.getPlansForPidWithStatus(
            pid,
            1,
            (res) => {
              if (res && res.length > 0) setPlansArr(getFilteredPlans(res));
              else
                EventService.emit("showError", {
                  message: `No active plans found for pid - ${pid}`,
                });
            },
            () =>
              EventService.emit("showError", {
                message: `Cannot retrieve plans for pid - ${pid}`,
              }),
          );
          commonServices.getAllFamilyMembers(
            pid,
            (res) => {
              if (res && res.families) {
                const withRel = res.families.map((el) => {
                  const rel = relations.find((e) => e.code == el.relation);
                  return { ...el, relationString: rel ? `(${rel.value})` : "" };
                });
                setFamilyMembers(withRel);
              }
            },
            () => {},
          );
        }
      },
      (err) => {
        EventService.emit("showError", {
          message: errorMessageHandler(err, "Failed to load patient details."),
        });
      },
    );
  };

  const accountCodes = Array.from(
    new Set(plansArr.map((p) => p?.planDetails?.accountCode).filter(Boolean)),
  );
  const accountNameFor = (code) => {
    if (!code) return "";
    // The patient-plan payload already carries the account name (planController
    // fills planDetails.accountName); prefer it, then fall back to account-configs.
    const fromPlan = plansArr.find((p) => p?.planDetails?.accountCode === code)
      ?.planDetails?.accountName;
    if (fromPlan) return fromPlan;
    const m = accountConfigs.find(
      (c) =>
        c && String(c.accountCode).toUpperCase() === String(code).toUpperCase(),
    );
    return (m && m.accountName) || "";
  };
  const plansForAccount = plansArr.filter(
    (p) => p?.planDetails?.accountCode === form.selectedAccountCode,
  );

  const selectAccount = (code) => {
    // Changing the account clears the plan and cascades a clear to everything below.
    setForm((prev) => ({
      ...prev,
      selectedAccountCode: code,
      selectedPlan: undefined,
    }));
    clearClaimDetailsBelowPlan();
    setFilteredFamily([]);
  };

  const selectPlan = (plan) => {
    // Changing the plan cascades a clear to every field below it.
    setForm((prev) => ({ ...prev, selectedPlan: plan }));
    clearClaimDetailsBelowPlan();
    if (!plan) return;
    if (plan?.planDetails?.reimbursementDetails?.isBankAccountRequired) {
      setBankResp(null);
      bankAccountService.fetchMaskedPrimaryBankAccountForPatient(
        form.pid,
        (res) => setBankResp(res),
        (err) => {
          if (err.code != 404) {
            EventService.emit("showError", {
              message:
                "Error fetching bank for the patient : " +
                errorMessageHandler(err).toString(),
            });
          }
        },
      );
    } else {
      // Plan doesn't need a bank account: drop anything fetched for an earlier plan
      // (and close its module) so nothing stale sits behind the hidden bank row.
      setBankResp(null);
      setOpenBankModal(false);
    }
    // filteredFamily is derived by an effect (below) from the plan + familyMembers,
    // so it stays correct even when family members load after the plan is selected
    // (e.g. when resuming a draft).
    api.getAllClaimTypes(
      plan?.planDetails?.id ?? -1,
      form.pid ?? -1,
      (res) => {
        if (res?.reimbursementTypes?.length > 0)
          setClaimTypesArr(res.reimbursementTypes);
        if (res?.claimConfig) setClaimConfig(res.claimConfig);
      },
      () =>
        EventService.emit("showError", {
          message: "Cannot retrieve claim types",
        }),
    );
  };

  const selectClaimType = (typeId) => {
    updateForm({ reimbursementTypeId: typeId });
    const t = claimTypesArr.find((e) => e.typeId == typeId);
    if (t) {
      setInstructions(t.instructions || []);
      updateForm({ reimbursementTypeName: t.typeName });
    }
  };

  // Invoice date bounds follow the selected claim type's config.
  useEffect(() => {
    const minDays =
      claimConfig?.invoiceMinDays && claimConfig.invoiceMinDays != 0
        ? claimConfig.invoiceMinDays
        : 3650;
    updateForm({
      minInvoiceDate: getMinInvoiceDate(minDays),
      maxInvoiceDate: getMaxInvoiceDate(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimConfig]);

  // "Applying claim for" options = Self + family members allowed by the plan.
  // Derived (not set inside selectPlan) so it recomputes when family members load
  // after the plan is chosen — e.g. when resuming a draft, where the plan is
  // auto-selected as soon as plans load, before the family list has arrived.
  useEffect(() => {
    const allowed =
      form.selectedPlan?.planDetails?.reimbursementDetails
        ?.allowedFamilyRelations ?? [];
    setFilteredFamily(
      (familyMembers ?? []).filter((e) => allowed.includes(e?.relation ?? -1)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyMembers, form.selectedPlan]);

  // ---- Documents ----
  const openUpload = () => uploadRef.current && uploadRef.current.click();

  const onFilesPicked = (event) => {
    const files = Array.from(event.target.files);
    const next = files.reduce((acc, file) => {
      if (file.size > 20000000) {
        EventService.emit("showError", {
          message: `File "${file.name}" is too large. Max allowed size is 20 MB.`,
        });
      } else {
        acc.push({
          name: file.name,
          status: "selected",
          url: URL.createObjectURL(file),
          fileObject: file,
        });
      }
      return acc;
    }, []);
    setDocs((prev) => [...prev, ...next]);
    if (next.length) markDirty();
    event.target.value = null;
  };

  const removeDoc = (event, index) => {
    event.stopPropagation();
    markDirty();
    setDocs((prev) => prev.filter((_, i) => i !== index));
  };

  const updateDoc = (index, updates) =>
    setDocs((prev) =>
      prev.map((d, i) => (i === index ? { ...d, ...updates } : d)),
    );

  const uploadSingleDoc = (index) => {
    const item = docs[index];
    if (!item || item.status === "uploaded") return;
    updateDoc(index, { status: "uploading" });
    const file = item.fileObject;
    const document = {
      title: file.name,
      description: "",
      url: "",
      type: 12,
      format: getDocumentFormat(extOf(file.name).toLowerCase()),
      status: 4,
    };
    api.uploadDocument(
      file,
      file.name,
      document,
      (res) => updateDoc(index, { uploadedDocId: res?.id, status: "uploaded" }),
      (err) => {
        setIsClicked(false);
        updateDoc(index, { status: "error" });
        EventService.emit("showError", {
          message:
            err?.message ?? "Something went wrong while uploading documents.",
        });
      },
      form.pid,
    );
  };

  // ---- Validation / payload ----
  const isValid = Boolean(
    form.pid &&
    form.patientName &&
    form.clientCode &&
    form.selectedPlan &&
    (form.claimedFor || form.claimedFor === 0) &&
    form.reimbursementTypeId &&
    form.claimAmount &&
    form.invoiceDate &&
    docs.length > 0 &&
    // App parity: eligibility must be resolved (so we send the exact amount the
    // create endpoint checks) and the wallet must allow filing.
    elig &&
    Boolean(elig.canFileClaim),
  );

  // Save-as-draft gating: a draft needs the identity fields (patient → account →
  // plan → applying-for → claim type); amount/invoice/docs are deferred (that's the
  // point of a draft). When resuming an existing draft, also require an actual edit.
  const draftRequiredFilled = Boolean(
    form.pid &&
    form.patientName &&
    form.selectedAccountCode &&
    form.selectedPlan &&
    (form.claimedFor || form.claimedFor === 0) &&
    form.reimbursementTypeId,
  );
  const canSaveDraft = draftRequiredFilled && (!draftId || dirty);

  const buildPayload = () => {
    const docsIds = docs
      .filter((d) => d.uploadedDocId)
      .map((d) => d.uploadedDocId);
    return {
      // Coerce numeric fields — native <select>/<input> yield strings, but the Go
      // ClaimV2 struct binds them as int64/float64 (a string 400s with
      // "unable to unmarshal request body").
      claimAmount: Number(form?.claimAmount) || 0,
      // Same as the member app: send the fetched eligible amount; the create
      // endpoint verifies it matches its own freshly-recomputed eligibility.
      eligibleClaimAmount: elig ? elig.eligibleAmount : 0,
      documentList: docsIds,
      reimbursementTypeName: form?.reimbursementTypeName,
      reimbursementTypeId: form?.reimbursementTypeId
        ? Number(form.reimbursementTypeId)
        : undefined,
      // "Claim received on" drives the claim's createdAt (a back-filled claim can be
      // received on a past date). We also send it as the invoice date, which the
      // create endpoint still requires — there's no separate invoice-date field yet.
      createdAtString: form?.invoiceDate,
      invoiceDateString: form?.invoiceDate,
      ignoreReimbursementLimit: form?.ignoreReimbursementLimit,
      documentType: form?.documentType ?? 0,
      claimedFor: form?.claimedFor,
      patientId: parseInt(form?.pid ?? "0"),
      planId: form.selectedPlan?.planDetails?.id,
      userComment: form?.userComment ?? "",
      ...(bankRequired ? { bankAccountId: bankResp?.id } : {}),
    };
  };

  const createReimbursement = () => {
    const allUploaded =
      docs.length > 0 && docs.every((d) => d.status === "uploaded");
    if (!allUploaded) return;
    // Synchronous guard: the [docs, isClicked] effect can re-enter while the
    // create is already in flight (e.g. a resumed draft whose docs are already
    // uploaded), which would file the same claim twice.
    if (submittingRef.current) return;
    submittingRef.current = true;
    api.createReimbursementClaim(
      buildPayload(),
      () => {
        setIsClicked(false);
        EventService.emit("showError", { message: "Claim filed successfully" });
        // Submitting a resumed draft: discard the draft now that a real claim exists.
        if (draftId)
          api.deleteClaimDraft(
            draftId,
            () => {},
            () => {},
          );
        goToDashboard();
      },
      (err) => {
        submittingRef.current = false; // allow a retry after a failure
        setIsClicked(false);
        EventService.emit("showError", {
          message:
            err?.message ?? "Something went wrong while filing the claim.",
        });
      },
    );
  };

  // Persist the draft (called directly, or by the effect below once uploads settle).
  const doSaveDraft = () => {
    api.saveClaimDraft(
      buildPayload(),
      () => {
        savingDraftRef.current = false;
        setSavingDraft(false);
        setDirty(false); // saved — needs a fresh edit before it can save again
        EventService.emit("showError", { message: "Draft saved" });
      },
      (err) => {
        savingDraftRef.current = false;
        setSavingDraft(false);
        EventService.emit("showError", {
          message: err?.message ?? "Failed to save draft.",
        });
      },
    );
  };

  // Once the user has hit Submit and every document has finished uploading, file it.
  useEffect(() => {
    if (
      isClicked &&
      docs.length > 0 &&
      docs.every((d) => d.status === "uploaded")
    ) {
      createReimbursement();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs, isClicked]);

  // A draft save requested with un-uploaded docs waits for the uploads to finish,
  // then persists. Aborts if any upload fails (uploadSingleDoc already surfaced it).
  useEffect(() => {
    if (!draftPendingSave) return;
    if (docs.some((d) => d.status === "error")) {
      savingDraftRef.current = false;
      setDraftPendingSave(false);
      setSavingDraft(false);
      return;
    }
    if (docs.length > 0 && docs.every((d) => d.status === "uploaded")) {
      setDraftPendingSave(false);
      doSaveDraft();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs, draftPendingSave]);

  // ---- Resume draft: fetch + prefill the form via the same cascade the user drives ----
  useEffect(() => {
    if (!draftId || !draftPid || draft) return;
    api.getReimbursementClaimById(
      draftPid,
      draftId,
      (res) => {
        if (res && res.claimId) {
          setDraft(res);
          setForm((prev) => ({
            ...prev,
            pid: String(res.patientId || draftPid),
            documentType: res.documentType || DOC_TYPE.SOFTCOPY,
          }));
        }
      },
      () =>
        EventService.emit("showError", {
          message: "Could not load the draft.",
        }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId, draftPid]);

  // draft + pid set -> run the patient lookup once (loads plans/family).
  useEffect(() => {
    if (draft && form.pid && !draftApplied.current.started) {
      draftApplied.current.started = true;
      loadPatient();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, form.pid]);

  // plans loaded -> select the draft's account + plan once (triggers claim-types fetch).
  useEffect(() => {
    if (draft && plansArr.length && !draftApplied.current.plan) {
      const plan = plansArr.find((p) => p?.planDetails?.id === draft.planId);
      if (plan) {
        draftApplied.current.plan = true;
        selectAccount(plan?.planDetails?.accountCode);
        selectPlan(plan);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, plansArr]);

  // claim types loaded -> select the draft's type + fill amount/date/docs once.
  useEffect(() => {
    if (draft && claimTypesArr.length && !draftApplied.current.type) {
      draftApplied.current.type = true;
      selectClaimType(draft.reimbursementTypeId);
      const dt =
        (draft.invoiceDate && String(draft.invoiceDate).slice(0, 10)) ||
        (draft.createdAt && String(draft.createdAt).slice(0, 10)) ||
        "";
      updateForm({
        claimAmount: draft.claimAmount,
        invoiceDate: dt && dt !== "0001-01-01" ? dt : undefined,
        claimedFor: draft.claimedFor,
        documentType: draft.documentType || DOC_TYPE.SOFTCOPY,
        ignoreReimbursementLimit: Boolean(draft.ignoreReimbursementLimit),
      });
      const docIds = draft.documentList || [];
      // Show placeholders immediately, then fetch each document's real name + URL
      // so the actual file (and image preview) renders, not just an id placeholder.
      setDocs(
        docIds.map((id) => ({
          name: "Document " + id,
          status: "uploaded",
          uploadedDocId: id,
        })),
      );
      const token = getDocToken();
      Promise.all(
        docIds.map(
          (id) =>
            new Promise((resolve) =>
              api.getDocuments(
                id,
                (res) => resolve({ id, res }),
                () => resolve({ id, res: null }),
              ),
            ),
        ),
      ).then((results) => {
        setDocs(
          results.map(({ id, res }) => ({
            name: (res && res.title) || "Document " + id,
            url: res && res.url ? res.url + "?token=" + token : undefined,
            status: "uploaded",
            uploadedDocId: id,
          })),
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, claimTypesArr]);

  const handleSubmit = () => {
    if (!isValid) {
      EventService.emit("showError", {
        message:
          "Please complete all required fields and attach at least one document.",
      });
      return;
    }
    setIsClicked(true);
    // Upload any not-yet-uploaded docs. The [docs, isClicked] effect fires
    // createReimbursement() once every doc is uploaded — which also covers the
    // already-uploaded (resumed-draft) case, so we must NOT also call it directly
    // here or the claim gets filed twice.
    docs.forEach((d, i) => {
      if (d.status !== "uploaded") uploadSingleDoc(i);
    });
  };

  const handleSaveDraft = () => {
    // Ignore a rapid second click while the first save is still in flight — the
    // ref flips synchronously, unlike the savingDraft state the button binds to.
    if (savingDraftRef.current) return;
    savingDraftRef.current = true;
    setSavingDraft(true);
    // Upload any not-yet-uploaded documents first so the draft persists their real
    // document ids (the effect above saves once every upload settles).
    const hasPending = docs.some((d) => d.status !== "uploaded");
    if (!hasPending) {
      doSaveDraft();
      return;
    }
    setDraftPendingSave(true);
    docs.forEach((d, i) => {
      if (d.status !== "uploaded") uploadSingleDoc(i);
    });
  };

  // Also disabled while a draft save is in flight, so the two actions can't overlap.
  const canSubmit = isValid && !isClicked && !savingDraft;
  const acceptTypes = "application/pdf,image/png,image/jpeg,image/jpg";
  // There's no cap on how many documents a claim can carry; the placeholder slots
  // only keep the grid from looking empty until MIN_DOC_SLOTS files are attached.
  const emptySlots = [];
  for (let i = docs.length + 1; i <= MIN_DOC_SLOTS; i += 1) emptySlots.push(i);

  return (
    <div className="file-claim-screen">
      {/* Header */}
      <div className="fc-header">
        <div className="fc-header-left">
          <div className="fc-back" onClick={goToDashboard}>
            <BackArrow />
          </div>
          <h2 className="fc-title">
            {readOnly
              ? "View draft"
              : draft
                ? "Resume draft"
                : "File new claim"}
          </h2>
        </div>
        <div className="fc-header-actions">
          {readOnly ? (
            <button className="fc-btn ghost" onClick={goToDashboard}>
              Back
            </button>
          ) : (
            <>
              {draftId ? (
                <button
                  className="fc-btn danger-outline"
                  onClick={() => setConfirmDeleteDraft(true)}
                  disabled={isClicked || savingDraft || deletingDraft}
                >
                  Delete draft
                </button>
              ) : (
                <button
                  className="fc-btn ghost"
                  onClick={goToDashboard}
                  disabled={isClicked || savingDraft}
                >
                  Cancel
                </button>
              )}
              <button
                className="fc-btn outline"
                onClick={handleSaveDraft}
                disabled={savingDraft || isClicked || !canSaveDraft}
              >
                {savingDraft ? "Saving…" : "Save as draft"}
              </button>
              <button
                className="fc-btn primary"
                onClick={handleSubmit}
                disabled={!canSubmit}
              >
                {isClicked ? "Submitting…" : "Submit claim"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="fc-body">
        {/* In view mode the whole form is disabled via this fieldset (display:contents
            keeps the flex layout intact); nothing inside can be edited. */}
        <fieldset className="fc-fieldset" disabled={readOnly}>
          {/* Patient details */}
          <div className="fc-card">
            <h3 className="fc-section-title">Patient details</h3>
            <div className="fc-grid">
              <div className="fc-field">
                <label className="fc-label">
                  Patient ID <span className="req">*</span>
                </label>
                <div className="fc-input-affix">
                  <input
                    type="number"
                    className="fc-input"
                    value={form.pid ?? ""}
                    onChange={(e) => {
                      markDirty();
                      updateForm({ pid: e.target.value });
                    }}
                    onKeyDown={(e) => e.key === "Enter" && loadPatient()}
                  />
                  <button
                    className="fc-affix-btn"
                    onClick={loadPatient}
                    title="Fetch patient"
                  >
                    {arrow}
                  </button>
                </div>
              </div>
              <div className="fc-field">
                <label className="fc-label">Patient name</label>
                <input
                  className="fc-input"
                  value={form.patientName ?? ""}
                  disabled
                  placeholder="—"
                />
              </div>

              <div className="fc-field">
                <label className="fc-label">
                  Account ID <span className="req">*</span>
                </label>
                <ThemedDropdown
                  value={form.selectedAccountCode}
                  onChange={(v) => {
                    markDirty();
                    selectAccount(v);
                  }}
                  disabled={accountCodes.length === 0}
                  placeholder={accountCodes.length ? "Select account" : "—"}
                  options={accountCodes.map((code) => {
                    const name = accountNameFor(code);
                    return {
                      value: code,
                      label: name ? `${code} — ${name}` : code,
                    };
                  })}
                />
              </div>
              <div className="fc-field">
                <label className="fc-label">Account name</label>
                <input
                  className="fc-input"
                  value={accountNameFor(form.selectedAccountCode)}
                  disabled
                  placeholder="—"
                />
              </div>

              <div className="fc-field">
                <label className="fc-label">
                  Plan ID <span className="req">*</span>
                </label>
                <ThemedDropdown
                  value={form.selectedPlan?.planDetails?.id}
                  onChange={(v) => {
                    markDirty();
                    selectPlan(
                      plansForAccount.find((p) => p?.planDetails?.id === v),
                    );
                  }}
                  disabled={!form.selectedAccountCode}
                  placeholder={form.selectedAccountCode ? "Select plan" : "—"}
                  options={plansForAccount.map((p) => {
                    const name = p?.planDetails?.basicDetails?.name;
                    return {
                      value: p?.planDetails?.id,
                      label: name
                        ? `${p?.planDetails?.id} — ${name}`
                        : String(p?.planDetails?.id),
                    };
                  })}
                />
              </div>
              <div className="fc-field">
                <label className="fc-label">Plan name</label>
                <input
                  className="fc-input"
                  value={
                    form.selectedPlan?.planDetails?.basicDetails?.name ?? ""
                  }
                  disabled
                  placeholder="—"
                />
              </div>
            </div>

            {bankRequired && Boolean(form.patientName) && (
              <div className="fc-bank-row">
                <div className="fc-bank-info">
                  <span className="fc-bank-title">Bank account</span>
                  <span className="fc-bank-value">
                    {bankResp && bankResp.id
                      ? `${
                          (bankResp.branchInfo &&
                            bankResp.branchInfo.bankName) ||
                          "Bank"
                        } ${maskedBankNum(bankResp.last4, bankResp.accNumLength)}`
                      : "No bank account added"}
                  </span>
                  {bankResp && bankResp.id && (
                    <span
                      className={
                        "fc-bank-badge " +
                        (BANK_STATUS_CLASS[bankResp.verificationStatus] ||
                          "unverified")
                      }
                    >
                      {BANK_STATUS_LABEL[bankResp.verificationStatus] ||
                        "Unverified"}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="fc-bank-btn"
                  onClick={() => setOpenBankModal(true)}
                >
                  {/* Verification isn't part of the filing flow (it's a later ops
                      step), so an existing account only offers "Change" here. A
                      rejected/missing account prompts adding a new one. */}
                  {bankResp && bankResp.id && bankResp.verificationStatus !== 2
                    ? "Change"
                    : "Add bank account"}
                </button>
              </div>
            )}
          </div>

          {/* Claim details */}
          <div className="fc-card">
            <h3 className="fc-section-title">Claim details</h3>
            <div className="fc-grid">
              <div className="fc-field">
                <label className="fc-label">
                  Applying claim for <span className="req">*</span>
                </label>
                <ThemedDropdown
                  value={form.claimedFor}
                  onChange={(v) => {
                    markDirty();
                    updateForm({ claimedFor: v });
                  }}
                  disabled={!(claimTypesArr.length > 0)}
                  placeholder="Select"
                  options={[
                    {
                      memberPatientId: 0,
                      firstName: "Self",
                      relationString: "",
                    },
                    ...(filteredFamily ?? []),
                  ].map((item) => ({
                    value: item.memberPatientId,
                    label: `${item.firstName} ${item.relationString}`.trim(),
                  }))}
                />
              </div>
              <div className="fc-field">
                <label className="fc-label">
                  Claim type <span className="req">*</span>
                </label>
                <ThemedDropdown
                  value={form.reimbursementTypeId}
                  onChange={(v) => {
                    markDirty();
                    selectClaimType(v);
                  }}
                  disabled={!(claimTypesArr.length > 0)}
                  placeholder="Select"
                  options={claimTypesArr.map((item) => ({
                    value: item.typeId,
                    label: item.typeName,
                  }))}
                />
              </div>

              <div className="fc-field">
                <label className="fc-label">
                  Claim received on <span className="req">*</span>
                </label>
                <input
                  type="date"
                  className="fc-input"
                  value={form.invoiceDate ?? ""}
                  min={form.minInvoiceDate ?? getMinInvoiceDate(3650)}
                  max={form.maxInvoiceDate ?? getMaxInvoiceDate()}
                  onChange={(e) => {
                    markDirty();
                    updateForm({ invoiceDate: e.target.value });
                  }}
                  disabled={!form.reimbursementTypeId}
                />
              </div>
              <div className="fc-field">
                <label className="fc-label">
                  Claim amount filed (₹) <span className="req">*</span>
                </label>
                <input
                  type="number"
                  className="fc-input"
                  value={form.claimAmount ?? ""}
                  onInput={(e) => {
                    e.target.value = Math.max(0, parseInt(e.target.value) || 0)
                      .toString()
                      .slice(0, 8);
                  }}
                  onChange={(e) => {
                    markDirty();
                    updateForm({ claimAmount: e.target.value });
                  }}
                  disabled={!form.reimbursementTypeId}
                />
                {SHOW_SUBLIMIT_EXCEPTION && (
                  <label className="fc-checkbox">
                    <input
                      type="checkbox"
                      checked={form.ignoreReimbursementLimit ?? false}
                      onChange={(e) => {
                        markDirty();
                        updateForm({
                          ignoreReimbursementLimit: e.target.checked,
                        });
                      }}
                    />
                    <span>Sublimit exception applicable</span>
                  </label>
                )}
              </div>
              <div className="fc-field">
                <label className="fc-label">
                  Eligible amount (₹)
                  {elig ? (
                    <button
                      type="button"
                      className="fc-info-btn"
                      title="How is this calculated?"
                      onClick={() => setShowEligInfo(true)}
                    >
                      <InfoIcon />
                    </button>
                  ) : null}
                </label>
                <input
                  className="fc-input"
                  value={
                    fetchingElig
                      ? "Calculating…"
                      : elig
                        ? String(elig.eligibleAmount)
                        : "—"
                  }
                  disabled
                  placeholder="—"
                />
                {elig && !elig.canFileClaim ? (
                  <div className="fc-elig-note error">
                    Not eligible to file this claim on the selected plan / claim
                    type.
                  </div>
                ) : elig &&
                  Number(form?.claimAmount) > (elig.eligibleAmount || 0) ? (
                  <div className="fc-elig-note">
                    Only ₹{elig.eligibleAmount} of the ₹
                    {Number(form?.claimAmount)} filed is eligible.
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {/* Required documents */}
          <div className="fc-card">
            <h3 className="fc-section-title">Required documents</h3>
            <div className="fc-muted">
              Attach the following for a smooth approval.
            </div>
            {instructions.length > 0 ? (
              <ul className="fc-req-list">
                {instructions.map((item, index) => (
                  <li key={index} className="fc-req-item">
                    <span className="fc-req-check">
                      <CheckIcon />
                    </span>
                    <span
                      className="fc-req-text"
                      dangerouslySetInnerHTML={{ __html: item?.instruction }}
                    />
                  </li>
                ))}
              </ul>
            ) : (
              <div className="fc-empty-hint">
                Select a claim type to see the documents required for it.
              </div>
            )}
          </div>

          {/* Upload documents */}
          <div className="fc-card">
            <h3 className="fc-section-title">Upload documents</h3>

            <div className="fc-doctype-label">Document type</div>
            <div className="fc-radio-group">
              <label className="fc-radio">
                <input
                  type="radio"
                  name="docType"
                  checked={form.documentType === DOC_TYPE.SOFTCOPY}
                  onChange={() => {
                    markDirty();
                    updateForm({ documentType: DOC_TYPE.SOFTCOPY });
                  }}
                />
                <span>
                  <b>Softcopy</b> All my claim documents are digital — there are
                  no hardcopies of the prescription or invoice.
                </span>
              </label>
              <label className="fc-radio">
                <input
                  type="radio"
                  name="docType"
                  checked={form.documentType === DOC_TYPE.HARDCOPY}
                  onChange={() => {
                    markDirty();
                    updateForm({ documentType: DOC_TYPE.HARDCOPY });
                  }}
                />
                <span>
                  <b>Hardcopy</b> Hardcopies exist and I will submit them after
                  soft-copy approval.
                </span>
              </label>
            </div>

            <div className="fc-doc-grid">
              {docs.map((doc, index) => {
                const showPreview =
                  isImageFile(doc) && doc.url && doc.status !== "error";
                return (
                  <div
                    key={index}
                    className={
                      "fc-doc-card" +
                      (doc.status === "error" ? " failed" : "") +
                      (showPreview ? " has-preview" : "")
                    }
                    onClick={() =>
                      doc.status !== "error" && window.open(doc.url, "_blank")
                    }
                  >
                    {showPreview && (
                      <img
                        className="fc-doc-preview"
                        src={doc.url}
                        alt={doc.name}
                      />
                    )}
                    <span className="fc-doc-badge">{extOf(doc.name)}</span>
                    {(doc.status === "selected" || doc.status === "error") && (
                      <button
                        className="fc-doc-remove"
                        onClick={(e) => removeDoc(e, index)}
                      >
                        &times;
                      </button>
                    )}
                    {doc.status === "uploading" ? (
                      <div className="fc-doc-icon">
                        <div className="fc-spinner" />
                      </div>
                    ) : doc.status === "error" ? (
                      <div className="fc-doc-icon">
                        <span
                          className="fc-doc-retry"
                          onClick={(e) => {
                            e.stopPropagation();
                            uploadSingleDoc(index);
                          }}
                          title="Retry upload"
                        >
                          <RetryIcon />
                        </span>
                      </div>
                    ) : showPreview ? null : (
                      <div className="fc-doc-icon">
                        <FileIcon />
                      </div>
                    )}
                    <div
                      className={
                        "fc-doc-name" + (showPreview ? " overlay" : "")
                      }
                    >
                      {doc.name}
                    </div>
                    {doc.status === "error" && (
                      <div className="fc-doc-status">UPLOAD FAILED</div>
                    )}
                    {doc.status === "uploaded" && !showPreview && (
                      <div className="fc-doc-status ok">Uploaded</div>
                    )}
                  </div>
                );
              })}
              {emptySlots.map((n) => (
                <div
                  key={"slot" + n}
                  className="fc-doc-slot"
                  onClick={openUpload}
                >
                  Document {n}
                </div>
              ))}
            </div>

            <input
              type="file"
              hidden
              multiple
              accept={acceptTypes}
              ref={uploadRef}
              onChange={onFilesPicked}
            />

            <div className="fc-upload-hint">
              Max 20 MB per file
            </div>
            <button className="fc-upload-btn" onClick={openUpload}>
              <UploadIcon />
              Upload documents
            </button>
          </div>
        </fieldset>
      </div>

      {/* Reused patientPanel bank-account module — same add / view / verify / change
          flow, keyed off the selected patient. On close, refresh the masked account
          so a newly added/changed account flows into the claim's bankAccountId. */}
      {bankRequired && openBankModal && (
        <BankAccount
          open={openBankModal}
          close={() => {
            setOpenBankModal(false);
            refreshMaskedBank(form.pid);
          }}
          patient={bankPatient}
        />
      )}

      {/* Eligibility calculation breakdown — mirrors the flutterzyla "how this works"
          info popup: eligible amount + available sub-limit / wallet, and the block note. */}
      {showEligInfo && elig ? (
        <div className="fc-info-overlay" onClick={() => setShowEligInfo(false)}>
          <div className="fc-info-card" onClick={(e) => e.stopPropagation()}>
            <button
              className="fc-info-close"
              onClick={() => setShowEligInfo(false)}
            >
              ×
            </button>
            <div className="fc-info-badge">
              <WalletIcon />
            </div>
            <div className="fc-info-title">Eligible claim amount</div>
            <div className="fc-info-amount">₹{elig.eligibleAmount ?? 0}</div>
            <div className="fc-info-box">
              <div className="fc-info-sub">How this works?</div>
              {elig.claim &&
              elig.claim.applicable &&
              elig.claim.sublimit &&
              elig.claim.sublimit.enabled ? (
                <p className="fc-info-line">
                  Available sub-limit: ₹
                  {(elig.claim.sublimitBalance &&
                    elig.claim.sublimitBalance.available) ||
                    0}{" "}
                  out of ₹
                  {(elig.claim.sublimitBalance &&
                    elig.claim.sublimitBalance.total) ||
                    elig.claim.sublimit.value ||
                    0}
                </p>
              ) : null}
              {elig.claim &&
              elig.claim.applicable &&
              elig.claim.perTransactionLimit &&
              elig.claim.perTransactionLimit.enabled ? (
                <p className="fc-info-line">
                  Per transaction limit: ₹
                  {elig.claim.perTransactionLimit.value || 0}
                </p>
              ) : null}
              {elig.claim &&
              elig.claim.applicable &&
              elig.claim.copayLimit &&
              elig.claim.copayLimit.enabled ? (
                <p className="fc-info-line">
                  Self Co-pay limit: {elig.claim.copayLimit.value || 0}%
                </p>
              ) : null}
              {elig.claim &&
              elig.claim.applicable &&
              ((elig.claim.sublimit && elig.claim.sublimit.enabled) ||
                (elig.claim.perTransactionLimit &&
                  elig.claim.perTransactionLimit.enabled) ||
                (elig.claim.copayLimit && elig.claim.copayLimit.enabled)) ? (
                <p className="fc-info-line">
                  Eligible wallet amount is minimum of the wallet rules
                  mentioned above
                </p>
              ) : null}
              <p className="fc-info-line">
                This amount will be blocked from wallet until claim is processed
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {/* Delete-draft confirmation (resume mode) */}
      {confirmDeleteDraft ? (
        <div
          className="fc-info-overlay"
          onClick={() => !deletingDraft && setConfirmDeleteDraft(false)}
        >
          <div className="fc-confirm-card" onClick={(e) => e.stopPropagation()}>
            <div className="fc-confirm-head">
              <h3>Delete this claim?</h3>
              <button
                className="fc-confirm-close"
                onClick={() => setConfirmDeleteDraft(false)}
                disabled={deletingDraft}
              >
                ×
              </button>
            </div>
            <p className="fc-confirm-body">
              Are you sure you want to delete this draft? This will permanently
              delete the claim, including all details and documents.
            </p>
            <div className="fc-confirm-foot">
              <button
                className="fc-btn ghost"
                onClick={() => setConfirmDeleteDraft(false)}
                disabled={deletingDraft}
              >
                Keep Claim
              </button>
              <button
                className="fc-btn danger"
                onClick={handleDeleteDraft}
                disabled={deletingDraft}
              >
                {deletingDraft ? "Deleting…" : "Delete claim"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default FileNewClaimScreen;
